/**
 * hrisStyleVars — the HRIS style model and its CSS custom properties.
 *
 * WHY IT IS ITS OWN FILE. `hrisSettings.ts` reaches SharePoint for the shared
 * config, which pulls in the SP client, which constructs MSAL at import — so
 * importing anything from it in a unit test fails on a missing tenant id
 * before a single assertion runs. The style layer is pure arithmetic over a
 * settings object and is the part worth pinning, so it lives here where a test
 * can reach it. Same split as payrollStepState.ts and leaveReconcile.ts.
 *
 * `hrisSettings` re-exports everything below, so no call site knows.
 */

/** Every HRIS screen that can carry its own style override. */
export type HrisScreenKey =
  | "dashboard"
  | "computation"
  | "payroll"
  | "bankfile"
  | "benefits"
  | "thirteenth"
  | "periods"
  | "payslips"
  | "reports"
  | "employees"
  | "attendance"
  | "leaves"
  | "loans"
  | "scoreboard";

export const HRIS_SCREENS: { key: HrisScreenKey; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "computation", label: "Payroll Computation" },
  { key: "payroll", label: "Payroll Workflow" },
  { key: "bankfile", label: "Bank File" },
  { key: "benefits", label: "Mandatory Benefits" },
  { key: "thirteenth", label: "13th Month Pay" },
  { key: "periods", label: "Weekly Payroll" },
  { key: "payslips", label: "Payslips" },
  { key: "reports", label: "Reports" },
  { key: "employees", label: "Employees" },
  { key: "attendance", label: "Attendance" },
  { key: "leaves", label: "Leaves & SIL" },
  { key: "loans", label: "Loans" },
  { key: "scoreboard", label: "KPI Scoreboard" },
];

/* ── Style settings — same mechanism as acctSettings/gridSettings ────────── */

// The Production Grid font stack — the app's reference typography.
export const PG_FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';
export const MONO_FONT = "'JetBrains Mono', 'Cascadia Mono', Consolas, monospace";
export const SERIF_FONT = "'Fraunces', Georgia, serif";
export const SANS_FONT = "'Hanken Grotesk', system-ui, sans-serif";

export const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: "Production Grid (system sans)", value: PG_FONT },
  { label: "Hanken Grotesk", value: SANS_FONT },
  { label: "Fraunces (serif)", value: SERIF_FONT },
  { label: "JetBrains Mono", value: MONO_FONT },
  { label: "Inter", value: "'Inter', system-ui, sans-serif" },
];

export interface IHrisColorSettings {
  /** Page background behind everything. */
  page: string;
  /** Card / panel surface. */
  surface: string;
  /** Inset tile (inputs, chips, tracks, workflow cards). */
  tile: string;
  /** Strong borders. */
  border: string;
  /** Row rules, card outlines. */
  borderSoft: string;
  /** Primary text. */
  text: string;
  /** Secondary text. */
  dim: string;
  /** Muted labels / captions. */
  faint: string;
  /** Khaki accent — the only accent color. */
  khaki: string;
  khakiLight: string;
  /** KPI card label / value colors. */
  kpiLabel: string;
  kpiValue: string;
}

export interface IHrisTypographySettings {
  /** Titles / section headings font family. */
  headingFont: string;
  /** Body text font family. */
  bodyFont: string;
  /** Numeric / tabular / uppercase-label font family. */
  monoFont: string;
  /** Module title ("Payroll Calc"). */
  titleSize: number;
  /** Uppercase panel titles ("WORKFLOW RUNS"). */
  panelTitleSize: number;
  tableFontSize: number;
  tableHeaderSize: number;
  kpiLabelSize: number;
  kpiValueSize: number;
}

export interface IHrisKpiSettings {
  paddingV: number;
  paddingH: number;
  cornerRadius: number;
  /** Khaki top-border thickness on KPI cards. 0 hides it. */
  topBorderWidth: number;
  /** Minimum card height in px (0 = auto). */
  minHeight: number;
}

/** Top bar action buttons (Export, Legacy page, mode toggle…). */
export interface IHrisButtonSettings {
  fontSize: number;
  paddingV: number;
  paddingH: number;
  cornerRadius: number;
}

export interface IHrisLayoutSettings {
  /** Space around screen content (body container). */
  pagePadding: number;
  /** Cards and panels (body + lower containers). */
  panelRadius: number;
  /** Table density. */
  rowPaddingV: number;
  rowPaddingH: number;
  /** Workflow step cards. */
  wfCardRadius: number;
  wfCardPadding: number;
}

export interface IHrisStyleSettings {
  colors: IHrisColorSettings;
  typography: IHrisTypographySettings;
  kpi: IHrisKpiSettings;
  buttons: IHrisButtonSettings;
  layout: IHrisLayoutSettings;
}

/**
 * The navy palette HRIS had before it adopted the payout skin.
 *
 * Kept as a named constant, not deleted, because it is sitting in people's
 * localStorage: every save round-tripped the then-defaults into the blob, so
 * a colour equal to one of these was never *chosen* — it is the old default
 * wearing a saved-setting's clothes. `hrisStyleToCssVars` treats it as unset
 * so the skin shows through, which is the difference between Mikko seeing the
 * new look and Mikko seeing no change at all.
 */
export const LEGACY_NAVY_COLORS: IHrisColorSettings = {
  page: "#081326",
  surface: "#0F1C36",
  tile: "#0f1e3c",
  border: "#21345c",
  borderSoft: "#1a2a4c",
  text: "#f5f7fa",
  dim: "#9aabcb",
  faint: "#5e72a0",
  khaki: "#c9a46a",
  khakiLight: "#dbc08a",
  kpiLabel: "#5e72a0",
  kpiValue: "#f5f7fa",
};

/**
 * DEFAULT style — the SUBCONTRACTORS skin (see hrisSkin.ts), in numbers.
 *
 * The colours are the skin's LIGHT values written as solid hex, because an
 * `<input type="color">` cannot show "follow the mode" — the picker has to
 * display something, and what it should display is what the screen actually
 * renders on opening. They are never emitted while they still equal these
 * values, so the live screen follows the mode rather than being pinned light.
 *
 * The sizes are BQC5's type scale (subconTheme.SCALE), which is the whole
 * point of the exercise: bigger, quieter type than HRIS carried before, one
 * face for headings, body and figures alike.
 */
export const DEFAULT_HRIS_STYLE: IHrisStyleSettings = {
  colors: {
    page: "#EEF0F3",
    surface: "#FFFFFF",
    tile: "#F3F4F6",
    border: "#D5D9DE",
    borderSoft: "#E3E6EA",
    text: "#14181F",
    dim: "#5B6472",
    faint: "#6E7887",
    khaki: "#8A6A2F",
    khakiLight: "#C9A46A",
    kpiLabel: "#5B6472",
    kpiValue: "#14181F",
  },
  typography: {
    headingFont: PG_FONT,
    bodyFont: PG_FONT,
    monoFont: PG_FONT,
    titleSize: 30,
    panelTitleSize: 12,
    tableFontSize: 14.5,
    tableHeaderSize: 13.5,
    kpiLabelSize: 11,
    kpiValueSize: 28,
  },
  kpi: { paddingV: 14, paddingH: 16, cornerRadius: 14, topBorderWidth: 0, minHeight: 0 },
  buttons: { fontSize: 13, paddingV: 8, paddingH: 14, cornerRadius: 8 },
  layout: { pagePadding: 24, panelRadius: 14, rowPaddingV: 12, rowPaddingH: 16, wfCardRadius: 14, wfCardPadding: 16 },
};

/**
 * Style → CSS custom properties, applied inline on every HRIS screen root.
 *
 * SIZES AND SPACING ONLY. Colour is the skin's (hrisSkin.ts) and follows the
 * light/dark switch; nothing here may write a colour.
 *
 * The module used to emit the whole palette from these settings, and the first
 * thing that did after the skin landed was make half the labels unreadable:
 * every Save round-tripped the then-current defaults into localStorage and into
 * the shared SharePoint blob, so colours nobody had chosen were still being
 * written onto the root, where they beat the mode and stranded a navy-tuned
 * grey on a paper page. Suppressing the ones that matched a known default was
 * not enough — a palette saved against some older default matched neither.
 *
 * So the rule is now absolute rather than conditional, which is also what the
 * SUBCONTRACTORS module this skin comes from does: it has no colour settings at
 * all. `IHrisColorSettings` survives so stored blobs still parse.
 */
export function hrisStyleToCssVars(style: IHrisStyleSettings): Record<string, string> {
  const t = style.typography, k = style.kpi, b = style.buttons, l = style.layout;
  return {
    "--hris-font-heading": t.headingFont,
    "--hris-font-body": t.bodyFont,
    "--hris-font-mono": t.monoFont,
    "--hris-title-size": `${t.titleSize}px`,
    "--hris-panel-title-size": `${t.panelTitleSize}px`,
    "--hris-table-font-size": `${t.tableFontSize}px`,
    "--hris-table-header-size": `${t.tableHeaderSize}px`,
    "--hris-kpi-label-size": `${t.kpiLabelSize}px`,
    "--hris-kpi-value-size": `${t.kpiValueSize}px`,
    "--hris-kpi-pad-v": `${k.paddingV}px`,
    "--hris-kpi-pad-h": `${k.paddingH}px`,
    "--hris-kpi-radius": `${k.cornerRadius}px`,
    "--hris-kpi-top-border": `${k.topBorderWidth}px`,
    "--hris-kpi-min-h": k.minHeight > 0 ? `${k.minHeight}px` : "auto",
    "--hris-btn-font-size": `${b.fontSize}px`,
    "--hris-btn-pad-v": `${b.paddingV}px`,
    "--hris-btn-pad-h": `${b.paddingH}px`,
    "--hris-btn-radius": `${b.cornerRadius}px`,
    "--hris-page-padding": `${l.pagePadding}px`,
    "--hris-panel-radius": `${l.panelRadius}px`,
    "--hris-row-pad-v": `${l.rowPaddingV}px`,
    "--hris-row-pad-h": `${l.rowPaddingH}px`,
    "--hris-wf-radius": `${l.wfCardRadius}px`,
    "--hris-wf-pad": `${l.wfCardPadding}px`,
  };
}
